import { useEffect, useRef, type ReactNode } from 'react'
import { Button } from './ui'

/**
 * "Are you sure?" — with the answer to "sure about what?" on the screen.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT window.confirm
 *
 * It was window.confirm until this file existed, and three things were wrong
 * with that. It renders as the browser's chrome, so the most consequential
 * control on the platform looked less considered than the rest of it. It is a
 * single line of plain text, so it could not distinguish "remove somebody from
 * a report" from "remove somebody's ability to read every company" — and those
 * are not the same question. And it blocks the main thread, so the row behind
 * it cannot even highlight to show which one is about to change.
 *
 * ---------------------------------------------------------------------------
 * CONSEQUENCE, NOT ACTION
 *
 * `detail` says what will be TRUE afterwards, not what the button does. "Ziad
 * will no longer be able to open Receivables" is checkable against the name the
 * administrator has in mind; "Remove access?" is not. That is the entire value
 * of a confirmation step, and a confirmation that only restates the button is a
 * click tax with no safety in it.
 *
 * Cancel is focused on open, so Enter is always the safe answer.
 */
export function ConfirmDialog({
  title,
  detail,
  confirmLabel,
  tone = 'danger',
  busy = false,
  confirmDisabled = false,
  focusInput = false,
  onConfirm,
  onCancel,
}: {
  title: string
  /** What will be true afterwards. Written for the person, not the database. */
  detail: ReactNode
  confirmLabel: string
  tone?: 'danger' | 'default'
  busy?: boolean
  /** The confirm button waits on something in the detail, such as a reason. */
  confirmDisabled?: boolean
  /** Focus the detail's first field, which the confirm button waits on, instead of Cancel. */
  focusInput?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const cancel = useRef<HTMLDivElement>(null)
  const body = useRef<HTMLDivElement>(null)
  const opener = useRef<Element | null>(null)
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    opener.current = document.activeElement
    // The SAFE control takes focus, so a held Enter key cannot confirm a
    // destructive action the reader has not looked at yet.
    // A disabled confirm cannot be pressed, so a field it waits on may take it.
    const field = focusInput ? body.current?.querySelector<HTMLElement>('input, textarea') : null
    ;(field ?? cancel.current?.querySelector<HTMLElement>('button'))?.focus()
    return () => {
      (opener.current as HTMLElement | null)?.focus?.()
    }
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCancel()
      }
      // Focus stays inside (review, 27 Oct 2026): tabbed out, a reader could pick
      // something behind the scrim while the question still stood over it.
      if (e.key === 'Tab') {
        const box = panel.current
        if (!box) return
        const stops = [...box.querySelectorAll<HTMLElement>('button, input, textarea, select, a[href], [tabindex]:not([tabindex="-1"])')]
          .filter((x) => !x.hasAttribute('disabled'))
        // While it works, both buttons are disabled: focus rests on the panel.
        if (stops.length === 0) {
          e.preventDefault()
          box.focus()
          return
        }
        const first = stops[0]
        const last = stops[stops.length - 1]
        const at = document.activeElement as HTMLElement | null
        const outside = !at || !box.contains(at)
        if (e.shiftKey ? (outside || at === first) : (outside || at === last)) {
          e.preventDefault()
          ;(e.shiftKey ? last : first).focus()
        }
      }
    }
    // Capture phase: this sits above a SlideOver, which also closes on Escape.
    // Without stopping propagation here, one press would dismiss both and the
    // administrator would lose a half-typed form to a cancelled confirmation.
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onCancel])

  return (
    <div className="fixed inset-0 z-40 grid place-items-center p-6">
      <div
        aria-hidden
        onClick={onCancel}
        className="anim-scrim-in absolute inset-0 bg-(--color-ink)/35"
      />
      <div
        ref={panel}
        tabIndex={-1}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="anim-panel-in relative w-full max-w-md overflow-hidden rounded-md focus:outline-none border border-(--color-line-strong) bg-(--color-surface) shadow-(--shadow-raised)"
      >
        <div className="px-5 py-4">
          <h2 className="text-(length:--text-lg) font-semibold tracking-[-0.01em] text-(--color-ink)">
            {title}
          </h2>
          <div ref={body} className="mt-2 text-(length:--text-base) leading-relaxed text-(--color-ink-2)">
            {detail}
          </div>
        </div>
        <div
          ref={cancel}
          className="flex items-center justify-end gap-2 border-t border-(--color-line) bg-(--color-surface-2) px-5 py-3.5"
        >
          <Button onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} disabled={busy || confirmDisabled}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
